import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Star, ArrowRight, Quote } from 'lucide-react';
import { testimonials } from '../data/mock';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const FadeInSection = ({ children, className = '', delay = 0 }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function TestimonialsPage() {
  return (
    <main className="pt-20">
      {/* Header */}
      <section className="py-16 bg-[rgb(17,17,19)] relative">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="max-w-[1200px] mx-auto px-6 relative">
          <FadeInSection>
            <span className="text-[#F37021] text-sm font-semibold tracking-widest uppercase">Testimonials</span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-4">Student Success Stories</h1>
            <p className="text-[rgb(161,161,170)] text-lg max-w-2xl">
              Hear from our students who've achieved their goals with Scaleup Academy's guidance.
            </p>
          </FadeInSection>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-20 bg-[rgb(17,17,19)]">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <FadeInSection key={t.id} delay={i * 0.08}>
                <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8 h-full flex flex-col hover:-translate-y-1 hover:border-[rgba(243,112,33,0.2)] transition-all duration-300 relative">
                  <Quote size={32} className="text-[rgba(243,112,33,0.15)] absolute top-6 right-6" />
                  <div className="flex gap-1 mb-5">
                    {Array.from({ length: 5 }).map((_, j) => (
                      <Star
                        key={j}
                        size={16}
                        className={j < t.rating ? 'text-[#F37021] fill-[#F37021]' : 'text-[rgb(63,63,63)]'}
                      />
                    ))}
                  </div>
                  <p className="text-[rgb(218,218,218)] text-sm leading-relaxed flex-1 mb-6">"{t.text}"</p>
                  <div className="flex items-center gap-3 pt-5 border-t border-white/10">
                    <div className="w-11 h-11 rounded-full bg-[rgba(243,112,33,0.1)] flex items-center justify-center text-[#F37021] font-semibold text-sm">
                      {t.avatar}
                    </div>
                    <div>
                      <div className="text-white font-medium text-sm">{t.name}</div>
                      <div className="text-[rgb(161,161,170)] text-xs">{t.course}</div>
                    </div>
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[rgb(26,28,30)]">
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <FadeInSection>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Write Your Success Story
            </h2>
            <p className="text-[rgb(161,161,170)] text-lg mb-8">
              Join the growing family of successful Scaleup Academy alumni.
            </p>
            <Link to="/contact">
              <Button className="bg-[#F37021] hover:bg-[#DC5A14] text-white font-semibold rounded-xl px-10 h-14 text-base border-0 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(243,112,33,0.35)]">
                Enroll Today <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </FadeInSection>
        </div>
      </section>
    </main>
  );
}
